import { ArrowDownTrayIcon, ArrowUturnLeftIcon, ArrowUturnRightIcon, CursorArrowRaysIcon, HandRaisedIcon, LightBulbIcon, SparklesIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { Fragment, memo, useState, type SVGProps } from 'react';
import { IconBox } from '../../components/IconBox';
import { ToolButton } from '../../components/ToolButton';
import { Tooltip } from '../../components/Tooltip';
import { t } from '../../potree/translation/markers';
import { T, useTranslator } from '../../translation/src';
import { useEditorState, useObjectEditor, type EditorTool } from './EditorContext';
import { SelectField, ToggleRow } from './panels/fields';
import { EDITOR_SHORTCUTS } from './useEditorShortcuts';

type IconProps = SVGProps<SVGSVGElement>;
const svg = (children: React.ReactNode) => (props: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" {...props}>
    {children}
  </svg>
);
const LineIcon = svg(<path d="M5 19L19 5" />);
const PolylineIcon = svg(<path d="M4 18L9 8l6 6 5-9" />);
/** Fountain-pen nib, tip to the bottom-left (like the pen tool in design apps). */
const PenIcon = svg(
  <g transform="rotate(45 12 12)">
    <path d="M12 22l-5-8 2-7h6l2 7z" />
    <path d="M12 22v-7" />
    <circle cx="12" cy="13.5" r="1.3" />
    <path d="M9 7V3.5h6V7" />
  </g>,
);
/** Hollow arrowhead with a point square (a design app's node tool): edits a shape's points and curve handles. */
const NodeIcon = svg(
  <>
    <path d="M5.5 4.5l12 6.5-5.2 1.3-2.3 5.2z" />
    <rect x="16" y="16" width="4.5" height="4.5" rx=".5" />
  </>,
);
const RectIcon = svg(<rect x="4" y="6" width="16" height="12" rx="1" />);
const CircleIcon = svg(<circle cx="12" cy="12" r="7.5" />);
const ArcIcon = svg(<path d="M5 19a14 14 0 0 1 14-14M5 19h1M19 5v1" />);
/** A horseshoe magnet, poles up: snapping. */
const MagnetIcon = svg(
  <>
    <path d="M4 13V5a2 2 0 0 1 4 0v8a4 4 0 0 0 8 0V5a2 2 0 0 1 4 0v8a8 8 0 0 1-16 0" />
    <path d="M4 8h4M16 8h4" />
  </>,
);
/** The magnet while some snapping is on: green, like an enabled toggle. */
const MagnetOnIcon = (props: IconProps) => <MagnetIcon {...props} className={classNames(props.className, 'text-neon-green-700 dark:text-neon-green-300')} />;

type ToolEntry = { tool: EditorTool; icon: React.FC<IconProps> | typeof CursorArrowRaysIcon; name: string; key: string };

/** Tool groups, separated by dividers: view, basic shapes, curves (draw + edit points). */
const TOOL_GROUPS: ReadonlyArray<ReadonlyArray<ToolEntry>> = [
  [
    { tool: 'select', icon: CursorArrowRaysIcon, name: t('select'), key: 'V' },
    { tool: 'pan', icon: HandRaisedIcon, name: t('pan'), key: 'H' },
  ],
  [
    { tool: 'line', icon: LineIcon, name: t('line'), key: 'L' },
    { tool: 'polyline', icon: PolylineIcon, name: t('polyline'), key: 'P' },
    { tool: 'rect', icon: RectIcon, name: t('rectangle'), key: 'R' },
    { tool: 'circle', icon: CircleIcon, name: t('circle'), key: 'C' },
    { tool: 'arc', icon: ArcIcon, name: t('arc'), key: 'A' },
  ],
  [
    { tool: 'pen', icon: PenIcon, name: t('pen (curves)'), key: 'B' },
    { tool: 'node', icon: NodeIcon, name: t('edit points'), key: 'N' },
  ],
];

export const Divider: React.FC = () => <div className="mx-1 h-6 w-px bg-gray-300 dark:bg-white/15" />;

/** What the hint bar says for each tool (translation keys). */
export const TOOL_HINTS: Readonly<Record<EditorTool, string>> = {
  select: t('click a shape to select it and drag to move it; use the gizmo to move, rotate and scale; drag the footprint edge handles to fit it to the drawing; right-click a shape for its actions'),
  node: t('click a shape, then drag its points and curve handles; double-click a curve point to make it sharp or smooth; Alt-drag a handle to break it'),
  pan: t('drag to move the view; scroll to zoom'),
  line: t('click the start and the end of the line; hold Shift for 45° steps'),
  polyline: t('click each point; click the first point to close, right-click, double-click or Enter to finish'),
  pen: t('click for a sharp point, drag for a smooth curve (Alt: break the handle); click the first point to close, right-click, double-click or Enter to finish'),
  rect: t('drag from one corner to the other'),
  circle: t('click the center, then the radius'),
  arc: t('click the center, then where the arc starts, then where it ends'),
};

const GRID_OPTIONS = [
  { value: '0', label: t('off') },
  { value: '0.01', label: '1 cm' },
  { value: '0.05', label: '5 cm' },
  { value: '0.1', label: '10 cm' },
];

const TooltipButton: React.FC<{ label: string; shortcut?: string; children: React.ReactNode }> = ({ label, shortcut, children }) => (
  <Tooltip
    delay={0}
    message={
      <span className="flex items-center gap-2">
        {label}
        {shortcut && <kbd className="rounded bg-gray-200 px-1 font-mono text-[10px] text-gray-700">{shortcut}</kbd>}
      </span>
    }
  >
    {children}
  </Tooltip>
);

/** Undo / redo of the draft (a smart object's AI changes are steps too). */
const UndoRedo_: React.FC = () => {
  const { store } = useObjectEditor();
  const { canUndo, canRedo } = useEditorState();
  const translate = useTranslator();
  return (
    <>
      <TooltipButton label={translate(t('undo'), { swc: true })} shortcut="Ctrl+Z">
        <ToolButton icon={ArrowUturnLeftIcon} name="undo" disabled={!canUndo} onClick={() => store.undo()} />
      </TooltipButton>
      <TooltipButton label={translate(t('redo'), { swc: true })} shortcut="Ctrl+Shift+Z">
        <ToolButton icon={ArrowUturnRightIcon} name="redo" disabled={!canRedo} onClick={() => store.redo()} />
      </TooltipButton>
    </>
  );
};
export const UndoRedo = memo(UndoRedo_);

/**
 * The drawing tools, import, the smart copy and undo/redo — rendered under
 * the active "Edit" mode tab in the header, like the project's persona tool rows.
 */
const EditorToolRow_: React.FC<{ onImport: () => void; onMakeSmart: () => void }> = ({ onImport, onMakeSmart }) => {
  const { ui, setUi } = useObjectEditor();
  const translate = useTranslator();
  return (
    <div className="flex items-center gap-2">
      {TOOL_GROUPS.map((group, i) => (
        <Fragment key={group[0].tool}>
          {i > 0 && <Divider />}
          {group.map((tool) => (
            <TooltipButton key={tool.tool} label={translate(tool.name, { swc: true })} shortcut={tool.key}>
              <ToolButton icon={tool.icon} name={tool.name} active={ui.tool === tool.tool} onClick={() => setUi((u) => ({ ...u, tool: tool.tool }))} />
            </TooltipButton>
          ))}
        </Fragment>
      ))}
      <Divider />
      <TooltipButton label={translate(t('make a smart copy: change it by describing what you want'), { swc: true })}>
        {/* Not a tool: never shown as active. */}
        <ToolButton icon={SparklesIcon} name={t('make a smart copy')} disabled={ui.preview !== null} onClick={onMakeSmart} />
      </TooltipButton>
      <Divider />
      <TooltipButton label={translate(t('import SVG, DXF or DWG'), { swc: true })}>
        <ToolButton icon={ArrowDownTrayIcon} name="import SVG, DXF or DWG" onClick={onImport} />
      </TooltipButton>
      <Divider />
      <UndoRedo />
    </div>
  );
};
export const EditorToolRow = memo(EditorToolRow_);

/** Snapping — at the right end of the tools strip, where projects keep tool settings. */
const EditorToolSettings_: React.FC = () => {
  const { ui, setUi } = useObjectEditor();
  const translate = useTranslator();
  const [open, setOpen] = useState(false);
  return (
    <div className="flex items-center gap-1">
      <div className="relative">
        <TooltipButton label={translate(t('snapping'), { swc: true })}>
          {/* The icon tells whether snapping is on (grid or points); the open panel shows itself. */}
          <ToolButton icon={ui.grid > 0 || ui.snapToPoints ? MagnetOnIcon : MagnetIcon} name="snapping" onClick={() => setOpen((o) => !o)} />
        </TooltipButton>
        {open && (
          <div className="absolute right-0 top-12 z-30 flex w-60 flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-eerie-black">
            <label className="flex flex-col gap-1 text-sm">
              <T _str="grid snapping" swc />
              <SelectField value={String(ui.grid)} options={GRID_OPTIONS.map((o) => ({ ...o, label: translate(o.label) }))} onChange={(v) => setUi((u) => ({ ...u, grid: Number(v) }))} />
            </label>
            <ToggleRow label={<T _str="snap to points" swc />} checked={ui.snapToPoints} onChange={(snapToPoints) => setUi((u) => ({ ...u, snapToPoints }))} />
          </div>
        )}
      </div>
    </div>
  );
};
export const EditorToolSettings = memo(EditorToolSettings_);

/** The shortcut sheet: a lightbulb in the canvas's bottom-right corner, like a project's shortcuts. */
const EditorShortcuts_: React.FC<{ className?: string }> = ({ className }) => {
  const translate = useTranslator();
  const [open, setOpen] = useState(false);
  return (
    <div className={classNames('z-10', className)}>
      {open && (
        <div className="absolute bottom-11 right-0 z-30 w-80 rounded-lg border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-eerie-black">
          <table className="w-full text-sm">
            <tbody>
              {EDITOR_SHORTCUTS.map((s) => (
                <tr key={s.keys}>
                  <td className="whitespace-nowrap py-0.5 pr-3 font-mono text-xs text-gray-500">{s.keys}</td>
                  <td className="py-0.5">{translate(s.label, { swc: true })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Tooltip message={<T _str="shortcuts" swc />} delay={0}>
        <IconBox icon={LightBulbIcon} highlight={open} onClick={() => setOpen((o) => !o)} className="rounded-lg" />
      </Tooltip>
    </div>
  );
};
export const EditorShortcuts = memo(EditorShortcuts_);
